import React, { useState, useEffect, useCallback } from 'react';
import { FileText, RefreshCw, AlertTriangle, Info } from 'lucide-react';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';

const fmt = (v) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ScheduleGPage = () => {
  const { clientId, clientName, hasActiveClient } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/schedule-g/${clientId}`);
      if (!res.ok) throw new Error(`Failed to load Schedule G (${res.status})`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!hasActiveClient) {
    return (
      <div className="p-6">
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>Select a client to view Schedule G.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const sections = data?.sections || [];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold">{data?.title || 'Schedule G'}</h1>
            <p className="text-sm text-gray-500">{clientName}</p>
          </div>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <Alert className="mb-4">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {data && !data.configured && (
        <Alert className="mb-4">
          <Info className="w-4 h-4" />
          <AlertDescription>
            Schedule G is not configured yet. Upload the account listing in the onboarding wizard
            (Schedule Configuration → Schedule G Listing).
          </AlertDescription>
        </Alert>
      )}

      {data?.configured && data?.config_status && data.config_status !== 'active' && (
        <Alert className="mb-4">
          <Info className="w-4 h-4" />
          <AlertDescription>
            Preview from a draft configuration. Activate the client config in the onboarding wizard to finalize.
          </AlertDescription>
        </Alert>
      )}

      {data?.amounts_pending && data?.configured && (
        <Alert className="mb-4">
          <Info className="w-4 h-4" />
          <AlertDescription>
            Showing the configured account layout. GL amounts are populated in the formatting pass.
          </AlertDescription>
        </Alert>
      )}

      {data?.configured && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {data.row_count} account(s) · {sections.length} subtotal group(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    {(data.columns || []).map((c, i) => (
                      <th
                        key={i}
                        className={`px-3 py-2 font-semibold border-b ${i >= 2 ? 'text-right' : ''}`}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sections.map((sec, si) => (
                    <React.Fragment key={si}>
                      {sec.rows.map((row) => (
                        <tr key={row.rowId} className="border-b hover:bg-blue-50/40">
                          <td className="px-3 py-1.5 font-mono text-gray-700">{row.gl_account}</td>
                          <td className="px-3 py-1.5">{row.description}</td>
                          <td className="px-3 py-1.5 text-right">{fmt(row.amount)}</td>
                          <td className="px-3 py-1.5 text-right text-gray-400">{fmt(null)}</td>
                          <td className="px-3 py-1.5 text-right">{fmt(row.amount)}</td>
                          <td className="px-3 py-1.5 text-gray-500">{row.notes}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold border-b-2 border-gray-300">
                        <td className="px-3 py-1.5" />
                        <td className="px-3 py-1.5">{sec.group}</td>
                        <td className="px-3 py-1.5 text-right">{fmt(sec.subtotal)}</td>
                        <td className="px-3 py-1.5 text-right">{fmt(null)}</td>
                        <td className="px-3 py-1.5 text-right">{fmt(sec.subtotal)}</td>
                        <td className="px-3 py-1.5" />
                      </tr>
                    </React.Fragment>
                  ))}
                  <tr className="bg-gray-900 text-white font-bold">
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2">Total Costs</td>
                    <td className="px-3 py-2 text-right">{fmt(data.grand_total)}</td>
                    <td className="px-3 py-2 text-right">{fmt(null)}</td>
                    <td className="px-3 py-2 text-right">{fmt(data.grand_total)}</td>
                    <td className="px-3 py-2" />
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScheduleGPage;
